import {useState, useEffect, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'

import { adminAPI, projectAPI } from '../../../api'

import { ContentFeatureSettings } from './ContentFeatureSettings'
import {API_URL} from '../../../api/endpoints'

import {Spinner} from 'react-bootstrap'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'

import {CopyToClipboard} from 'react-copy-to-clipboard'

import { ConfirmModal } from '../../../components/ConfirmModal'
import { APIAccessEditModal } from './APIAccessEditModal'
import { convertToDate, convertToDateTime } from '../../../utils'

import toastr from 'toastr'

import BootstrapDateRangePicker from '../../../components/BootstrapDateRangePicker'

import { Line } from 'react-chartjs-2'


interface Props {
}

interface ChartData {
  labels: string[];
  data: { [key: string]: number[] };
}



const RequestChat = ({logs, title}) => {

  const chartRef = useRef(null)
  const [chartData, setChartData] = useState<{
    labels: string[];
    datasets: { label: string; data: number[] }[];
  } | null>(null)

  useEffect(() => { 
    if (logs) {
      const datasets = Object.entries(logs.data).map(([label, data]) => ({
        label,
        data: data as number[],
        borderWidth:1,
        fill: true
      }));
      const data = {
        labels: logs.labels,
        datasets,
      };
      setChartData(data);
    } else {
      setChartData(null);
    }
  },[logs])


  return <>
    <div className="h6 mt-4">
      {title}
    </div>
    { chartData ?
      <Line
        ref={chartRef}
        data={chartData || { labels: [], datasets: [] }}
        height="35"
        options={{
          plugins: {
            tooltip: {
              mode: 'index',
            },
            title: {
              display: false,
            },
            datalabels: {
              display: (context: any) => {
                return context.dataset.data[context.dataIndex] !== 0;
              },
              color: "black", 
              font: {
                size: 12,
              },
            },
          },
          scales: {
            x: {
              ticks: {
                maxTicksLimit: 10,
                display: true,
              },
              display: true,
              stacked: true,
            },
            y: {
              beginAtZero: true,
              stacked: true,
              display: true,
            },
          },
          responsive: true,
        }}
      />
    :
      <div className="col-12 mt-2">
        <div className="alert alert-primary">No logs</div>
      </div>
    }
    </>
}

const APIAccess : React.FC<Props> = ({}) => {

  const { projectId } = useParams<{ projectId: string}>();
  const [project, setProject] = useState<any>(null)


  const navigate = useNavigate();
  const [showEditModal, setShowEditModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)

  const [showDeactivateModal, setShowDeactivateModal] = useState<boolean>(false)
  const [showRegenerateModal, setShowRegenerateModal] = useState<boolean>(false)

  const [isCodeCopied, setIsCodeCopied] = useState(false)

  const [apiRateLimit, setApiRateLimit] = useState<string>()

  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [timezoneOffset, setTimezoneOffset] = useState<number>()

  const [logsForStatusCode, setLogsForStatusCode] = useState<ChartData>()
  const [logsForEndpoint, setLogsForEndpoint] = useState<ChartData>()
  const [logsLoaded, setLogsLoaded] = useState<boolean>(false)
  

  useEffect(() => {
    loadLogs()
  }, [startDate, endDate, timezoneOffset])

  const handleInputChange = (e) => {
    const value = e.target.value
    setApiRateLimit(value)
  }

  const handleUpdateRateLimitClick = async () => {
    if (typeof apiRateLimit === 'number') {
      await updateAPIRateLimit(project.id, project.api_access_id, apiRateLimit);
    } else {
      await updateAPIRateLimit(project.id, project.api_access_id, apiRateLimit ? apiRateLimit.trim() : null);
    }
  }

  const handleUpdateStatus = async (status) => {
    await projectAPI.updateSetting(project.id, 'api_access', {'id':project.api_access_id, 'attribute_name': 'status', 'attribute_value': status})
    toastr.info("Updated")
    setProject({...project, api_access_status: status})
  }

  const handleRegenerate = () => {
    projectAPI.regenerateAPIAccess(project.id)
    toastr.info("Regenerated")
    loadProject()
  }

  useEffect(() => {
    loadProject()
  }, [projectId])


  async function loadProject(){
    if( projectId ){
      let {data: data} = await adminAPI.getProject(projectId)
      setProject(data['data'])
      setApiRateLimit(data['data']['api_access_rate_limit_per_minute'])
    }
  }

  const handleDelete = async () => {
    navigate('../api-accesses')
  }

  const updateAPIRateLimit = async (projectId:string, apiAccessId: number, value: string | null) => {
    await projectAPI.updateSetting(projectId, 'api_access', {'id':apiAccessId, 'attribute_name': 'rate_limit_per_minute', 'attribute_value': value})
    toastr.info("Updated")
  }
  
  const deleteProject = () => {
    projectAPI.delete_(project.id).then(() => {
      handleDelete && handleDelete()
    })
  }

  const handleCopy = () => {
    setIsCodeCopied(true);
    setTimeout(() => {
      setIsCodeCopied(false);
    }, 5000);
  };

  const handleDateSelection = (startDate: string, endDate:string, timezoneOffset:number) => {
    setStartDate(startDate)
    setEndDate(endDate)
    setTimezoneOffset(timezoneOffset)
  }

  async function loadLogs () {
    if( startDate && endDate && timezoneOffset && projectId ){
      setLogsLoaded(false)
      let {data: data} = await projectAPI.getAPIRequestLogs(projectId, startDate, endDate, timezoneOffset)
      if( data['data'] ){
        setLogsForStatusCode(data['data']['status_code'])
        setLogsForEndpoint(data['data']['endpoint'])
      }else{
        setLogsForStatusCode(undefined)
        setLogsForEndpoint(undefined)
      }
      setLogsLoaded(true)
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between mb-5">
        <button className="btn btn-sm btn-light-dark" onClick={()=>navigate('../api-accesses')}>
          Back to list
        </button>
      </div>
      {project ?
        <div className="card p-0">
          <div className="card-header border-0">
            <div className="card-title align-items-start flex-column pt-5">
              <span className={`card-label fw-bold fs-3 ${project.description && project.description != '' && 'mb-1'}`}>
                {project.for_load_testing &&
                <span className="badge badge-warning me-2">
                  Load Testing
                </span>
                }
                {project.name}
              </span>
              {project.description && project.description != '' &&
              <span className="text-muted mt-1 fw-semibold fs-7">{project.description}</span>
              }
            </div>
            <div className="card-toolbar">
              { project.deleted_dt == null &&
                <>
                  <button className="btn btn-sm btn-light-primary me-2" onClick={()=>setShowEditModal(true)}>Edit</button>
                  { showEditModal && 
                    <APIAccessEditModal show={showEditModal} project={project} handleClose={() => setShowEditModal(false)} handleUpdated={()=> {
                      loadProject()
                      setShowEditModal(false)
                    }}/>
                  }
                  <button className="btn btn-sm btn-light-danger" onClick={()=>setShowDeleteModal(true)}>Delete</button>
                  <ConfirmModal show={showDeleteModal} handleConfirm={deleteProject} handleClose={() => setShowDeleteModal(false)}/>
                </>
              }
            </div>
          </div>
          <div className="card-body pt-0">
            <div className="row bg-gray-100 p-5 rounded">
              <div className="col-12 col-md-1">
                <div className="fs-6 fw-bold">Created/Approved</div>
                <div>{project.created_by_email}</div>
                <div>{convertToDate(project.created_dt)}</div>
              </div>
              { project.deleted_dt && (
              <div className="col-12 col-md-1">
                <div className="fs-6 fw-bold text-danger">Deleted</div>
                <div>{project.deleted_by_email}</div>
                <div>{convertToDate(project.deleted_dt)}</div>
              </div>
              )}
              { project.api_access_request_full_name &&
                <>
                  <div className="col-12 col-md-1">
                    <div className="fs-6 fw-bold">Request Created Admin</div>
                    <div>{project.api_access_request_created_by_email && project.api_access_request_created_by_email}</div>
                    <div>{project.api_access_request_created_dt && convertToDate(project.api_access_request_created_dt)}</div>
                  </div>
                  <div className="col-12 col-md-10 d-flex flex-column">
                    <div className='fs-6 fw-bold'>Request Details</div>
                    <div className="d-flex flex-md-row flex-column">
                      <div className="min-w-200px mb-1 mb-md-0 pe-2">
                        <div className="fs-6">Full name</div>
                        <div>{project.api_access_request_full_name && project.api_access_request_full_name}</div>
                      </div>
                      <div className="min-w-200px mb-1 mb-md-0 pe-2">
                        <div className="fs-6">Email</div>
                        <div>{project.api_access_request_email && project.api_access_request_email}</div>
                      </div>
                      <div className="min-w-200px mb-1 mb-md-0 pe-2">
                        <div className="fs-6">Organization</div>
                        <div>{project.api_access_request_organization && project.api_access_request_organization}</div>
                      </div>
                      <div className="min-w-300px mb-1 mb-md-0">
                        <div className="fs-6">Use case</div>
                        <div>{project.api_access_request_use_case && project.api_access_request_use_case}</div>
                      </div>
                    </div>
                  </div>
                </>
              }
            </div>
            <div className="row border rounded mt-5 p-5">
              <div className="col-lg-2">
                <div className="fs-6 fw-bold"># Requests</div>
                <div>{project.number_of_api_requests ? project.number_of_api_requests.toLocaleString() : 0}</div>
              </div>
              <div className="col-lg-2">
                <div className="fs-6 fw-bold">Latest Request On</div>
                <div>{convertToDateTime(project.latest_api_request_dt)}</div>
              </div>
              { 
              !project.deleted_dt 
              &&
              <>
                <div className="col-lg-2">
                  <div className="fs-6 fw-bold">
                    API Rate Limit
                    <OverlayTrigger
                      key="api-rate-limit"
                      placement="top"
                      overlay={
                        <Tooltip id="api-rate-limit">
                          Per minute. When empty, the rate limit will not be applied.
                        </Tooltip>
                      }
                    >
                      <i className="fa-solid fa-info-circle fs-7 ms-2"></i>
                    </OverlayTrigger>
                  </div>
                  <div className="d-flex align-items-center border border-gray-300 mw-100px rounded mb-1">
                    <input 
                      type='number' 
                      className="form-control border-0 form-control-transparent form-control-sm" 
                      value={apiRateLimit}
                      onChange={handleInputChange}
                    />
                  </div>
                  <button type='button' className='btn btn-light-dark btn-sm fs-8 px-2 py-1' onClick={handleUpdateRateLimitClick}>
                    Update
                  </button>
                </div>
                <div className="col-lg-2">
                  <div className="fs-6 fw-semibold flex-center d-flex flex-row flex-start">
                    <div className="me-1 fw-bold">API Key</div>
                    <span className={`badge badge-light-${project.api_access_status == 'active' ? 'primary' : 'danger'}`}>
                      {project.api_access_status}
                    </span>
                  </div>
                  <CopyToClipboard text={project.api_key} onCopy={handleCopy}>
                    <div className="d-flex align-items-center border border-gray-300 mw-250px rounded mb-1">
                      <input type='password' className="form-control-sm border-0 form-control-transparent form-control w-100" value={project.api_key} disabled/>
                      <button type='button' className='btn btn-clean btn-sm btn-icon'>
                        <i className={`fa-solid fa-${isCodeCopied ? 'check' : 'copy'} text-primary fs-7`}></i>
                      </button>
                    </div>
                  </CopyToClipboard>
                  { project.api_access_status == 'active' &&
                  <>
                    <button type='button' className='btn btn-primary btn-sm fs-8 px-2 py-1 me-1' onClick={() => setShowRegenerateModal(true)}>
                      Regenerate
                      <OverlayTrigger
                        key="regenerating"
                        placement="top"
                        overlay={
                          <Tooltip id="regenerating">
                            Regenerating will deactivate the current API key.
                          </Tooltip>
                        }
                      >
                        <i className="fa-solid fa-info-circle fs-7 ms-1 pe-0"></i>
                      </OverlayTrigger>
                    </button>
                    <ConfirmModal 
                      show={showRegenerateModal} 
                      handleConfirm={()=>{
                        handleRegenerate()
                        setShowRegenerateModal(false)
                      }} 
                      handleClose={() => setShowRegenerateModal(false)}
                    />
                    <button type='button' className='btn btn-danger btn-sm fs-8 px-2 py-1' onClick={()=>setShowDeactivateModal(true)}>
                      Deactivate 
                      <OverlayTrigger
                        key="deactivating"
                        placement="top"
                        overlay={
                          <Tooltip id="deactivating">
                            Deactivating will invalidate the API key, but it can be reactivated.
                          </Tooltip>
                        }
                      >
                        <i className="fa-solid fa-info-circle fs-7 ms-1 pe-0"></i>
                      </OverlayTrigger>
                    </button>
                    <ConfirmModal 
                      show={showDeactivateModal} 
                      handleConfirm={()=>{
                        setShowDeactivateModal(false)
                        handleUpdateStatus('inactive')
                      }}
                      handleClose={() => setShowDeactivateModal(false)}
                    />
                  </>
                }
                { project.api_access_status == 'inactive' &&
                <button type='button' className='btn btn-primary btn-sm fs-8 px-2 py-1' onClick={()=>handleUpdateStatus('active')}>
                  Activate
                  <OverlayTrigger
                    key="activating"
                    placement="top"
                    overlay={
                      <Tooltip id="activating">
                        Activating will reactivate the API key.
                      </Tooltip>
                    }
                  >
                    <i className="fa-solid fa-info-circle fs-7 ms-1 pe-0"></i>
                  </OverlayTrigger>
                </button>
                }
              </div>
              {project.for_load_testing &&
                <>
                  <div className="col-lg-4">
                    <div className="fs-6 mb-1 fw-bold">
                      Load Testing Endpoint through Fanar-API
                      <OverlayTrigger
                        key="load-testing-endpoint"
                        placement="top"
                        overlay={
                          <Tooltip id="load-testing-endpoint">
                            This endpoint with the project ID must be used with a load-testing user account's access token.
                          </Tooltip>
                        }
                      >
                        <i className="fa-solid fa-info-circle fs-7 ms-2"></i>
                      </OverlayTrigger>
                    </div>
                    <code className="mx-0 p-1">{API_URL}/{project.id}/chats/&#123;endpoint or new or chat_id&#125;</code>            
                  </div>
                </>
              }
              </>
              }
              <div className="col-12 mt-5">
                <div className="d-flex flex-row align-items-center">
                  <BootstrapDateRangePicker onDateSelect={handleDateSelection} minDate={project.created_dt}/>
                  {!logsLoaded && (
                    <Spinner animation='border' className='ms-4'/>
                  )}
                </div>
                <div>
                  {logsLoaded && <RequestChat logs={logsForStatusCode} title="HTTP Status Code"/>}
                </div>
                <div className="mt-5 border-top">
                  {logsLoaded && <RequestChat logs={logsForEndpoint} title="Endpoints (HTTP 200)"/>}
                </div>
              </div>
            </div>
            { 
            !project.deleted_dt 
            && 
            <>
              <div className="row bg-gray-100 p-5 mt-5 rounded">
                <div className="col-12">
                  <ContentFeatureSettings project={project} setProject={setProject} />
                </div>
              </div>
            </>
            }
          </div>
        </div>
        : <Spinner animation='border' className="ms-5"/>}
    </div>
  )
}

export default APIAccess